import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import Fuse from "fuse.js";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Photo from "../components/Photo";
import "./main.css";
import "./blogs.css";

const ITEMS_PER_PAGE = 9; // 3 колонки * 3 ряда

const Main = () => {
  const [allBlogs, setAllBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [apiUrl, setApiUrl] = useState("");

  useEffect(() => {
    const fetchApiUrl = async () => {
      try {
        const response = await axios.get("/templates/api.txt");
        setApiUrl(response.data.trim());
      } catch (err) {
        console.error("Ошибка при загрузке API URL:", err);
        setError("Не удалось загрузить конфигурацию API");
      }
    };
    fetchApiUrl();
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      if (!apiUrl) return;

      try {
        const response = await axios.get(`${apiUrl}api/blogs`);

        if (response.data && response.data.blogs) {
          setAllBlogs(response.data.blogs);
          setFilteredBlogs(response.data.blogs);
        } else {
          setError("Некорректный формат данных блогов");
        }
      } catch (err) {
        setError("Ошибка загрузки блогов");
        console.error("Ошибка при загрузке блогов:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [apiUrl]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredBlogs(allBlogs);
      return;
    }

    const fuse = new Fuse(allBlogs, {
      keys: ["title", "description"],
      threshold: 0.3,
    });

    const results = fuse.search(searchQuery);
    setFilteredBlogs(results.map((result) => result.item));
  }, [searchQuery, allBlogs]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const totalPages = Math.ceil(filteredBlogs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentBlogs = filteredBlogs.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const generatePageNumbers = () => {
    let start = Math.max(1, currentPage - 2);
    let end = start + 4;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - 4);
    }
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  if (loading) return <div className="loading"></div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="main-content">
      <Helmet>
        <title data-react-helmet="true">
          Блог о сигаретах и табачных изделиях | Nicotine Club
        </title>
        <meta
          data-react-helmet="true"
          name="description"
          content="Полезные статьи и новости о сигарах, табаке и аксессуарах. Экспертные мнения и рекомендации от Nicotine Club."
        />
      </Helmet>

      <div className="blogs-page">
        <h1 className="blogs-h1">Блог о сигаретах</h1>
        <input
          type="text"
          placeholder="Поиск по статьям..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input search-blogs bmb-50"
        />
        <div className="blogs-grid">
          {currentBlogs.length > 0 ? (
            currentBlogs.map((blog) => (
              <div key={blog.id} className="blogs-card">
                <Link to={`/blogs/${blog.link}`} className="blogs-link">
                  <Photo
                    src={`${apiUrl}img/${blog.img_url}`}
                    alt={blog.title}
                    className="blogs-image"
                  />
                  <div className="blogs-content">
                    <h3 className="blogs-title">{blog.title}</h3>
                    <div className="blogs-meta">
                      <span className="blogs-date">
                        {new Date(blog.created_at).toLocaleDateString("ru-RU", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    <p className="blogs-description">{blog.description}</p>
                  </div>
                </Link>
              </div>
            ))
          ) : (
            <div className="no-results">
              {allBlogs.length === 0
                ? "Нет статей для отображения."
                : "По вашему запросу ничего не найдено."}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Назад
            </button>

            <div className="page-numbers">
              {generatePageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  disabled={page === currentPage}
                  className={`pagination-button ${
                    page === currentPage ? "active" : ""
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Далее
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Main;
